/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnChanges,
  ViewChild,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
/***********************/
/* BFX */
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  ICAutocompleteGenericModel,
  ICTenorValueDateModel,
} from "./models/bfx-interface-controls.models";
import { ICNInputControlState } from "./models/bfx-interface-controls.enums";
// models

export const VALUE_DATE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxValueDateComponent),
  multi: true,
};

import { SysFeatures, SystemConfigPromiseService } from "shared/cubiq-services";
import { OENEntryType } from "app/order-entry/data/enums/oe-entry-type.enum";
import { OENLockedState } from "../../data/enums/oe-locked-state.enum";
import { LoggerService } from "@bfx/logger";
import { BfxAutoCompleteComponent } from "../../../bfx-element-library/bfx-autocomplete.component";
import { BfxCalendarComponent } from "./good-from-until/bfx-calendar.component";

const logger = LoggerService.for(__dirname);

@Component({
  selector: "bfx-value-date",
  templateUrl: "./html/bfx-value-date.html",
  providers: [VALUE_DATE_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxValueDateComponent implements OnInit, ControlValueAccessor, OnChanges {
  @Input() inputId: string;

  @Input("label") input_label: string;

  @Input("disabled") input_disabled: boolean;

  @Input("entryType") entry_type: OENEntryType;

  @Input() scrollHeight: string = "200px";

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onModelChange: (_: any) => void = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onModelTouched: () => void = () => {};

  @Output() reEvaluateTrigger: EventEmitter<any> = new EventEmitter<any>();

  @Output() reEvaluateFixingDate: EventEmitter<Date> = new EventEmitter<Date>();

  @ViewChild("autocompleteControl", { static: true })
  autocompleteControl: ElementRef;

  protected input_dataset: CubiqTS.CurrencyPairCalendarImpl;

  get classes(): Record<string, boolean> {
    return {
      [`interface-control__value-date__container`]: true,
      [`interface-control__value-date__container--valid`]:
        this.data.state === 0 && !this.locked && this.data.allTenorValueDates != null,
      [`interface-control__value-date__container--invalid`]:
        this.data.state === 1 && !this.locked && this.data.allTenorValueDates != null,
      [`interface-control__value-date__container--active`]:
        (this.data.state === 2 && !this.locked && this.data.allTenorValueDates != null) ||
        this.inputFocused,
      [`interface-control__value-date__container--inactive`]:
        this.data.state === 3 && !this.locked && this.data.allTenorValueDates == null,
      [`interface-control__value-date__container--locked`]: this.locked,
    };
  }

  /* External Validation - not integrated in code */
  input_externalState: ICNInputControlState;

  @Input()
  get externalState() {
    return this.input_externalState;
  }

  set externalState(value: any) {
    this.input_externalState = value;
  }

  @Input()
  public get dataset() {
    return this.input_dataset;
  }

  public set dataset(value: any) {
    this.input_dataset = value;
    if (this.input_dataset) {
      let prov: Array<ICTenorValueDateModel> = [];
      let tenorFound: boolean = false;
      let rePopulate: boolean = false;
      switch (this.entry_type) {
        case OENEntryType.NDF:
        case OENEntryType.NDF_ALGO:
          if (
            this.input_dataset.tenorToValueAndFixingDateSet instanceof Array &&
            this.input_dataset.tenorToValueAndFixingDateSet.length > 0
          ) {
            // GUARD
            this.input_dataset.tenorToValueAndFixingDateSet.forEach((element) => {
              const elValueDate = element.valueDate;
              const elFixDate = element.fixingDate;
              const valueDate = new Date(elValueDate.year, elValueDate.month - 1, elValueDate.day);
              const fixingDate = new Date(elFixDate.year, elFixDate.month - 1, elFixDate.day);
              let tvd = new ICTenorValueDateModel(element.tenor, valueDate, fixingDate);
              prov.push(tvd);
              if (this.data.value == null) {
                if (CubiqTS.Tenor.M1 == element.tenor) {
                  tenorFound = true;
                  rePopulate = true;
                  this.data.value = tvd;
                }
              } else if (this.data.value && this.data.value.tenor == element.tenor) {
                tenorFound = true;
                this.data.value.valueDate = valueDate;
                this.data.valueDate = valueDate;
                this.data.value.fixingDate = fixingDate;
                this.onChange(this.data.value);
              }
            });
          }
          break;
        case OENEntryType.SPOT:
        default:
          if (
            this.input_dataset.tenorToValueDateSet instanceof Array &&
            this.input_dataset.tenorToValueDateSet.length > 0
          ) {
            // GUARD
            this.input_dataset.tenorToValueDateSet.forEach((element) => {
              if (
                (!this.data.value || this.input_dataset.tenorToValueDateSet.length === 1) &&
                element.tenor === CubiqTS.Tenor.SPOT
              ) {
                tenorFound = true;
                rePopulate = true;
                this.data.value = new ICTenorValueDateModel(
                  element.tenor,
                  new Date(
                    element.valueDate.year,
                    element.valueDate.month - 1,
                    element.valueDate.day,
                  ),
                );
                this.onChange(this.data.value);
              } else if (this.data.value && this.data.value.tenor == element.tenor) {
                tenorFound = true;
                this.data.value.valueDate = new Date(
                  element.valueDate.year,
                  element.valueDate.month - 1,
                  element.valueDate.day,
                );
                //this.data.valueDate=this.data.value.valueDate;
                this.onChange(this.data.value);
              }
              if (CubiqTS.Tenor.SPOT === element.tenor || this.fwdEnabled) {
                prov.push(
                  new ICTenorValueDateModel(
                    element.tenor,
                    new Date(
                      element.valueDate.year,
                      element.valueDate.month - 1,
                      element.valueDate.day,
                    ),
                  ),
                );
              }
            });
          }
          this.updateInternalLockAccordingToDatasetSize();
          break;
      }
      if (prov instanceof Array && prov.length > 0) {
        // GUARD
        this.data.allTenorValueDates = prov;
        if (this.input_dataset.holidays) {
          let holidays: Array<Date> = this.input_dataset.holidays.map((standardDate) => {
            let hol = new Date(
              standardDate.date.year,
              standardDate.date.month - 1,
              standardDate.date.day,
            );
            if (
              this.data.value &&
              this.data.value.tenor == undefined &&
              this.data.value.valueDate
            ) {
              if (hol.valueOf() == this.data.value.valueDate.valueOf()) {
                this.data.value = null;
                this.onChange(this.data.value);
                this.data.state = ICNInputControlState.INVALID;
                if (OENEntryType.NDF == this.entry_type) {
                  this.reEvaluateFixingDate.emit(null);
                }
              }
            }
            return hol;
          });
          if (OENEntryType.NDF == this.entry_type) {
            // add days from today to spot next as these shouldnt be allowed either
            const snDate = this.getSNDate(prov);
            if (snDate) {
              let now = new Date();
              for (let d = now; d < snDate; d.setDate(d.getDate() + 1)) {
                holidays.push(new Date(d));
              }
            }
          }

          this.data.holidays = holidays;
        }
        if (this.data.value && this.data.value.tenor != undefined && !tenorFound) {
          if (!tenorFound) {
            this.data.value = null;
            this.onChange(this.data.value);
            this.data.state = ICNInputControlState.INVALID;
          }
          if (rePopulate) {
            this.onChange(this.data.value);
          }
        }
      }
    } else {
      this.data.holidays = null;
      this.data.allTenorValueDates = null;
      this.data.tenorValueDates = null;
      switch (this.entry_type) {
        case OENEntryType.NDF:
        case OENEntryType.NDF_ALGO:
          this.data.value = new ICTenorValueDateModel(CubiqTS.Tenor.M1, null);
          break;
        default:
          this.data.value = new ICTenorValueDateModel(CubiqTS.Tenor.SPOT, null);
          break;
      }
      this.onChange(this.data.value);
      this.updateInternalLockAccordingToDatasetSize();
    }
    this.searchTenors();
    this.validateState();
  }

  protected getSNDate(models: ICTenorValueDateModel[]): Date {
    const snTenor = models.find((model) => model.tenor === CubiqTS.Tenor.SN);
    if (snTenor) {
      return snTenor.valueDate;
    }
    return null;
  }

  /***************************************/
  /* DATA */
  public config: any = {
    dateTimeFormats: ["DD MMM YYYY", "DD-MMM-YYYY", "DD/MMM/YYYY"],
  };

  public data: any = {
    //valueText: '',
    value: null, // ICTenorValueDateModel,
    disabled: true,
    label: "Value date",
    tenorValueDates: [],
    valueDate: null,
    holidays: [],
    state: ICNInputControlState.INACTIVE,
    autoCompleteInputPosition: null,
  };

  public locked: boolean = false;
  public internalLockState: OENLockedState = OENLockedState.UNLOCKED;
  public externalLockState: OENLockedState = OENLockedState.UNLOCKED;
  public inputFocused: boolean = false;
  protected fwdEnabled: boolean = false;
  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    public element: ElementRef,
    public translationService: TranslateService,
    protected sysConfigService: SystemConfigPromiseService,
  ) {}

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */
  ngOnInit() {
    this.initializeOnInit();
  }

  ngOnChanges() {
    this.initializeOnChanges();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  protected initializeOnInit(): void {
    this.initialize();
    /*if(this.data.value != null && this.data.value instanceof BfxAutocompleteGenericModel){
			this.data.valueText = this.data.value.label;
		} else{
			this.data.value = new BfxAutocompleteGenericModel(null, '');
			this.data.valueText = this.data.value.label;
		} */
  }

  protected initializeOnChanges(): void {
    if (this.input_externalState != null) {
      this.data.state = this.externalState;
    }

    this.initialize();
  }

  protected initialize(): void {
    this.initializeReadOnlyFlag();

    if (this.data.value != null && this.data.value instanceof ICAutocompleteGenericModel) {
      //this.data.valueText = this.data.value.label;
      this.validateState();
    } else {
      //	this.data.valueText = this.data.label;
    }

    if (this.input_disabled != null && typeof this.input_disabled === "boolean") {
      this.data.disabled = this.input_disabled;
    }

    if (this.input_label != null && this.input_label !== "") {
      this.data.label = this.input_label;
    }
  }

  /**
   * Determine whether control is readonly - this can be overidden in e.g. NDF subclass to
   * use different criteria for whether control is readonly e.g. 1mNDFOnly config
   */
  protected initializeReadOnlyFlag() {
    this.sysConfigService.getSystemConfig(SysFeatures.fwdEnabled).then((value) => {
      this.fwdEnabled = "Y" == value || "y" == value ? true : false;
      if (!this.fwdEnabled) {
        // Disable when fwds are not supported - otherwise leave at value that was there
        this.data.disabled = true;
      }
      if (!this.fwdEnabled) this.lockInputControl();
    });
  }

  public searchTenors(filter: { query: string } = { query: "" }): void {
    if (this.data.allTenorValueDates) {
      let query: string = filter.query.toLowerCase();
      if (query.length > 0) {
        this.data.tenorValueDates = this.data.allTenorValueDates.filter((tenor: any) =>
          tenor.label.toLowerCase().includes(query),
        );
      } else {
        this.data.tenorValueDates = this.data.allTenorValueDates.slice(0);
      }
    }
  }

  /**********************************************************************/
  /* HANDLERS */
  public resetInputControl(): void {
    //this.data.valueText = '';
    this.data.value = null;
    this.data.disabled = true;
    this.data.label = "Value date";
    this.data.state = this.data.state = ICNInputControlState.INACTIVE;
    this.externalState = null;
    this.dataset = null;
    //this.onChange(this.data.value);
    this.data.state = ICNInputControlState.INACTIVE; //  no error until someone touches it
  }

  /**********************************************************************/
  /* LOCKING */
  public lockInputControl(): void {
    this.externalLockState = OENLockedState.LOCKED;
    this.applyLocking();
  }

  public unlockInputControl(): void {
    this.externalLockState = OENLockedState.UNLOCKED;
    this.applyLocking();
  }

  protected updateInternalLockAccordingToDatasetSize() {
    if (
      this.input_dataset != null &&
      Array.isArray(this.input_dataset.tenorToValueDateSet) &&
      this.input_dataset.tenorToValueDateSet.length === 1 &&
      this.data.valueDate != null
    ) {
      this.internalLockState = OENLockedState.LOCKED;
    } else {
      this.internalLockState = OENLockedState.UNLOCKED;
    }
    this.applyLocking();
  }

  protected applyLocking(): void {
    if (this.externalLockState === OENLockedState.LOCKED) {
      // external LOCK, internal ANY
      this.locked = true;
    } else if (this.internalLockState === OENLockedState.LOCKED) {
      // external UNLOCKED, internal LOCKED
      this.locked = true;
    } else {
      // external UNLOCKED, internal UNLOCKED
      this.locked = false;
    }
  }

  /**********************************************************************/
  /* EVENTS */

  public onFocus(event: any): void {
    this.inputFocused = true;
    this.data.state = this.fwdEnabled ? ICNInputControlState.ACTIVE : ICNInputControlState.INACTIVE;
    this.onChange(this.data.value);
  }

  public onBlur(event: any): void {
    this.inputFocused = false;
    this.data.state = ICNInputControlState.INACTIVE;
    this.validate(event);
    this.onChange(event);
  }

  public onChange(event: any): void {
    if (this.data.value) {
      this.data.valueDate = this.data.value.valueDate;
    } else {
      this.data.valueDate = null;
    }
    this.onModelChange(this.data.value);
    this.validateState();
  }

  public onSelect(event: any): void {
    this.onChange(event);
    this.reEvaluateTrigger.emit(true);
    if (OENEntryType.NDF == this.entry_type) {
      this.reEvaluateFixingDate.emit(null);
    }
  }

  /**********************************************************************/
  /* HANDLERS */

  writeValue(value: any): void {
    this.data.value = value;
    //this.data.valueText = value != null ? value.label : '';
    if (this.data.value) {
      this.data.valueDate = this.data.value.valueDate;
    } else {
      this.data.valueDate = null;
    }
    this.updateInternalLockAccordingToDatasetSize();
    this.validateState();
  }

  registerOnChange(fn: () => void): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* METHODS */

  public validateState(): void {
    this.externalState = null;
    if (this.inputFocused) {
      this.data.state = this.fwdEnabled
        ? ICNInputControlState.ACTIVE
        : ICNInputControlState.INACTIVE;
    } else if (
      !this.inputFocused &&
      (this.data.value == null ||
        !(this.data.value instanceof ICTenorValueDateModel) ||
        this.vdIsInvalid(this.data.value))
    ) {
      this.data.state = ICNInputControlState.INVALID;
    } else if (
      !this.inputFocused &&
      this.data.value != null &&
      this.data.value instanceof ICTenorValueDateModel
    ) {
      this.data.state = ICNInputControlState.VALID;
    }
  }

  private vdIsInvalid(vd: ICTenorValueDateModel): boolean {
    if (vd && vd.tenor == undefined) {
      if (vd.valueDate) {
        vd.valueDate.setHours(0, 0, 0, 0);
        let now: Date = new Date();
        now.setHours(0, 0, 0, 0);
        if (vd.valueDate.valueOf() < now.valueOf()) {
          return true;
        }
      }
    }
    return false;
  }

  showCalendarValue(dataset: Date): void {
    this.assignToModel(dataset);
  }

  assignToModel(setValue: Date): void {
    this.data.valueDate = setValue;
    this.data.value = new ICTenorValueDateModel(null, setValue);
  }

  brokenDateSelected(newDate: Date): void {
    this.assignToModel(newDate);
    this.onChange(this.data.value);
    this.reEvaluateTrigger.emit(true);
    if (OENEntryType.NDF == this.entry_type) {
      this.reEvaluateFixingDate.emit(null);
    }
  }

  tenorValueDateClick($event): void {
    if (!this.locked && this.data.allTenorValueDates != null) {
      let fd = this.data.value.fixingDate;
      this.assignToModel(this.data.valueDate);
      this.onChange(this.data.value);
      if (OENEntryType.NDF == this.entry_type && this.data.value) {
        this.reEvaluateFixingDate.emit(fd);
      }
    }
  }

  /******************************************************************************/
  public validate(event: any): void {
    if (this.data.value != null && this.data.value !== "") {
      let tenorValid: boolean = this.isTenorValid(this.data.value);
      if (!tenorValid) {
        let currentValueDateStr: string =
          this.data.valueDate != null
            ? CubiqTS.moment(this.data.valueDate).format("DD-MMM-YYYY")
            : null;
        if (currentValueDateStr != this.data.value) {
          if (this.isDateTimeValidMoment(this.data.value)) {
            let newDate = CubiqTS.moment(
              this.data.value,
              this.config.dateTimeFormats,
              true,
            ).toDate();

            if (
              !this.data.holidays ||
              this.data.holidays.length == 0 ||
              this.data.holidays.find(
                (holidayDate) => holidayDate.getTime() == newDate.getTime(),
              ) == undefined
            ) {
              this.brokenDateSelected(newDate);
            } else {
            }
          }
        }
      }
    }
  }

  public isTenorValid(value: any, caseInsensitive: boolean = false): boolean {
    if (value instanceof ICTenorValueDateModel) {
      return true;
    } else if (value instanceof String) {
      let dataset = this.data.allTenorValueDates;
      return dataset != null && dataset.length > 0
        ? dataset.find(
            (item) =>
              item.label === value ||
              (caseInsensitive && item.label.toLowerCase() === value.toLowerCase()),
          ) != null
        : false;
    }
    return false;
  }

  public isDateTimeValidMoment(dateTime: string, strict: boolean = true): boolean {
    try {
      return dateTime != null && dateTime !== ""
        ? CubiqTS.moment(dateTime, this.config.dateTimeFormats, strict).isValid()
        : false;
    } catch (ex) {
      return false;
    }
  }

  onFieldIconClick(
    $event: { event: Event; type: "start" | "end"; index: number },
    autocomplete: BfxAutoCompleteComponent,
    calendar: BfxCalendarComponent,
  ) {
    if ($event.type === "end" && $event.index === 1) {
      this.data.state = ICNInputControlState.ACTIVE;
      calendar.onButtonClick($event.event);
    }
  }
}
